import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Polyline,
  MarkerF,
} from "@react-google-maps/api";
import Select from "react-select";
import { baseURL } from "../../Utils/constants/constant";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { GetRequest } from "../../Utils/hooks/ApiHandler";
import { FormGroup, FormLabel } from "react-bootstrap";
import { Controller, useForm, Watch } from "react-hook-form";
import { customStyles } from "../../Utils/helpers/commonhelper";

const mapContainerStyle = { width: "100%", height: "100%" };

const parseJSON = (str, fallback = {}) => {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

// Extract lat/lng from a parsed point object — handles both field name formats
const extractLatLng = (obj) => {
  if (!obj) return null;
  const lat = parseFloat(
    obj.latitude ?? obj.start_lat ?? obj.lat ?? obj.Latitude,
  );
  const lng = parseFloat(
    obj.longitude ?? obj.start_lng ?? obj.lng ?? obj.Longitude,
  );
  if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) return { lat, lng };
  return null;
};

const buildRouteFromTrip = (trip) => {
  if (!trip) return [];
  const points = [];

  if (trip.start_point) {
    const p = extractLatLng(parseJSON(trip.start_point));
    if (p) points.push(p);
  } else if (trip.mid_point?.length > 0) {
    const p = extractLatLng(parseJSON(trip.mid_point[0].mid_points));
    if (p) points.push(p);
  }

  if (Array.isArray(trip.mid_point) && trip.mid_point.length > 0) {
    [...trip.mid_point]
      .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
      .forEach((mp) => {
        const p = extractLatLng(parseJSON(mp.mid_points));
        if (p) points.push(p);
      });
  }

  if (trip.end_point) {
    const p = extractLatLng(parseJSON(trip.end_point));
    if (p) points.push(p);
  } else if (trip.mid_point?.length > 0) {
    const last = trip.mid_point[trip.mid_point.length - 1];
    const p = extractLatLng(parseJSON(last.mid_points));
    if (p) points.push(p);
  }

  const unique = [];
  points.forEach((p) => {
    const prev = unique[unique.length - 1];
    if (!prev || prev.lat !== p.lat || prev.lng !== p.lng) unique.push(p);
  });
  return unique;
};

const formatDate = (ts) => {
  if (!ts) return "--";
  return new Date(parseInt(ts)).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};
const formatTime = (ts) => {
  if (!ts) return "--";
  return new Date(parseInt(ts)).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};
const formatDateKey = (ts) => {
  if (!ts) return "Unknown";
  const d = new Date(parseInt(ts));
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};
const calcDuration = (start, end) => {
  if (!start || !end) return "--";
  const diff = Math.abs(parseInt(end) - parseInt(start));
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  return hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
};
const toInputDate = (ts) => {
  if (!ts) return "";
  const d = new Date(parseInt(ts));
  return d.toISOString().split("T")[0];
};

// Group trips by date label
const groupTripsByDate = (trips) => {
  const groups = {};
  [...trips]
    .sort((a, b) => parseInt(b.start_time) - parseInt(a.start_time))
    .forEach((trip) => {
      const key = formatDateKey(trip.start_time);
      if (!groups[key]) groups[key] = [];
      groups[key].push(trip);
    });
  return groups;
};

const ITEMS_PER_PAGE = 10;

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .trk-root {
    background: #f0f2f5;
    min-height: 100vh;
    padding: 16px 20px;
    font-family: 'Inter', sans-serif;
  }

  /* ── TOP BAR ── */
  .trk-topbar {
    background: #fff;
    border-radius: 12px;
    padding: 12px 18px;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    box-shadow: 0 1px 3px rgba(0,0,0,0.07);
    border: 1px solid #e5e7eb;
  }
  .trk-topbar-left { display: flex; align-items: center; gap: 10px; }
  .trk-icon {
    width: 34px; height: 34px;
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    border-radius: 8px; display: flex; align-items: center;
    justify-content: center; font-size: 15px;
    box-shadow: 0 2px 6px rgba(59,130,246,0.3);
    flex-shrink: 0;
  }
  .trk-title { font-size: 16px; font-weight: 700; color: #111827; }
  .trk-sub   { font-size: 11px; color: #9ca3af; }
  .trk-live  {
    display: flex; align-items: center; gap: 6px;
    background: #f0fdf4; border: 1px solid #bbf7d0;
    color: #16a34a; font-size: 11px; font-weight: 600;
    padding: 4px 11px; border-radius: 20px; letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .trk-live-dot {
    width: 7px; height: 7px; background: #22c55e;
    border-radius: 50%; animation: blink 1.8s infinite;
  }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }

  /* ── FILTER ICON BUTTON (in topbar) ── */
  .trk-filter-icon-btn {
    display: flex; align-items: center; gap: 6px;
    background: #f9fafb;
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    position: relative;
  }
  .trk-filter-icon-btn:hover { background: #eff6ff; border-color: #93c5fd; color: #3b82f6; }
  .trk-filter-icon-btn.active { background: #eff6ff; border-color: #3b82f6; color: #3b82f6; }
  .trk-filter-dot {
    position: absolute; top: -4px; right: -4px;
    width: 8px; height: 8px;
    background: #3b82f6; border-radius: 50%;
    border: 2px solid #fff;
  }
  .trk-topbar-right { display: flex; align-items: center; gap: 10px; }

  /* ── FILTER POPUP OVERLAY ── */
  .trk-filter-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.25);
    z-index: 1000;
    display: flex; align-items: flex-start; justify-content: flex-end;
    padding: 70px 20px 0;
    backdrop-filter: blur(2px);
  }
  .trk-filter-popup {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 20px;
    width: 300px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.15);
    animation: popIn 0.15s ease;
  }
  @keyframes popIn { from { opacity:0; transform: translateY(-8px) scale(0.97); } to { opacity:1; transform: none; } }
  .trk-popup-title {
    font-size: 13px; font-weight: 700; color: #111827;
    margin-bottom: 16px;
    padding-bottom: 10px;
    border-bottom: 1px solid #f3f4f6;
    display: flex; align-items: center; gap: 6px;
  }
  .trk-popup-field { margin-bottom: 14px; }
  .trk-popup-label { font-size: 11px; font-weight: 600; color: #6b7280; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.5px; }
  .trk-date-input {
    width: 100%;
    border: 1.5px solid #d1d5db;
    border-radius: 7px;
    padding: 7px 10px;
    font-size: 12px;
    font-family: 'Inter', sans-serif;
    color: #111827;
    background: #f9fafb;
    outline: none;
    cursor: pointer;
    transition: border-color 0.15s;
    box-sizing: border-box;
  }
  .trk-date-input:focus { border-color: #3b82f6; background: #fff; }
  .trk-popup-actions { display: flex; gap: 8px; margin-top: 18px; }
  .trk-filter-btn {
    flex: 1; padding: 8px 0;
    border-radius: 7px;
    font-size: 12px; font-weight: 600;
    font-family: 'Inter', sans-serif;
    cursor: pointer; border: none;
    transition: background 0.15s;
  }
  .trk-filter-btn.apply { background: #3b82f6; color: #fff; }
  .trk-filter-btn.apply:hover { background: #2563eb; }
  .trk-filter-btn.reset { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
  .trk-filter-btn.reset:hover { background: #e5e7eb; }
  .trk-syncing { font-size: 12px; color: #3b82f6; display: flex; align-items: center; gap: 5px; }

  /* ── 3-PANEL LAYOUT ── */
  .trk-panels {
    display: grid;
    grid-template-columns: 260px 1fr 280px;
    gap: 14px;
    align-items: start;
    min-height: calc(100vh - 180px);
  }

  /* ── LEFT PANEL: trip list ── */
  .trk-left {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    display: flex;
    flex-direction: column;
    height: calc(100vh - 195px);
    min-height: 500px;
  }
  .trk-left-header {
    padding: 12px 14px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 11px;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 1px;
    background: #fafafa;
    flex-shrink: 0;
  }
  .trk-list-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
  }
  .trk-list-scroll::-webkit-scrollbar { width: 4px; }
  .trk-list-scroll::-webkit-scrollbar-track { background: transparent; }
  .trk-list-scroll::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }

  /* Date group header */
  .trk-date-group { padding: 6px 14px 4px; }
  .trk-date-group-label {
    font-size: 10px;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    padding: 4px 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .trk-date-group-label::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #f3f4f6;
  }
  .trk-date-badge {
    display: inline-block;
    background: #f3f4f6;
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 9px;
    color: #9ca3af;
    font-weight: 600;
  }

  /* Trip row */
  .trk-trip-row {
    margin: 2px 8px;
    padding: 9px 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
    border: 1.5px solid transparent;
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }
  .trk-trip-row:hover { background: #f8faff; border-color: #dbeafe; }
  .trk-trip-row.active { background: #eff6ff; border-color: #3b82f6; }
  .trk-trip-num {
    width: 24px; height: 24px;
    background: #f3f4f6;
    border-radius: 6px;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700;
    color: #6b7280;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .trk-trip-row.active .trk-trip-num { background: #3b82f6; color: #fff; }
  .trk-trip-body { flex: 1; min-width: 0; }
  .trk-trip-mode {
    font-size: 11px; font-weight: 600;
    color: #374151;
    display: flex; align-items: center; gap: 4px;
    margin-bottom: 2px;
  }
  .trk-trip-row.active .trk-trip-mode { color: #1d4ed8; }
  .trk-trip-times { font-size: 10px; color: #9ca3af; }
  .trk-trip-dist {
    font-size: 10px; font-weight: 600;
    color: #16a34a;
    background: #f0fdf4;
    border-radius: 4px;
    padding: 1px 5px;
    margin-top: 3px;
    display: inline-block;
  }

  /* ── PAGINATION ── */
  .trk-pagination {
    padding: 10px 12px;
    border-top: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    background: #fafafa;
  }
  .trk-page-info { font-size: 11px; color: #9ca3af; }
  .trk-page-info b { color: #374151; }
  .trk-page-btns { display: flex; gap: 4px; align-items: center; }
  .trk-page-btn {
    width: 28px; height: 28px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
    font-size: 12px;
    color: #374151;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Inter', sans-serif;
    transition: background 0.12s, border-color 0.12s;
  }
  .trk-page-btn:hover:not(:disabled) { background: #eff6ff; border-color: #93c5fd; color: #3b82f6; }
  .trk-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .trk-page-btn.active { background: #3b82f6; border-color: #3b82f6; color: #fff; }

  /* ── CENTER: MAP ── */
  .trk-center {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    height: calc(100vh - 195px);
    min-height: 500px;
    position: relative;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }
  .trk-map-chips {
    position: absolute; top: 12px; left: 12px; z-index: 10;
    display: flex; gap: 6px; flex-wrap: wrap;
  }
  .trk-chip {
    background: rgba(255,255,255,0.97);
    backdrop-filter: blur(6px);
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 11px; font-weight: 600;
    color: #374151;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  }
  .trk-chip.blue { color: #3b82f6; border-color: #bfdbfe; background: rgba(239,246,255,0.97); }
  .trk-empty-map {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    flex-direction: column; gap: 10px; color: #9ca3af; font-size: 13px;
  }
  .trk-spinner {
    width: 32px; height: 32px;
    border: 2px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .trk-no-route {
    position: absolute; inset: 0;
    background: rgba(255,255,255,0.7);
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 6px; color: #9ca3af; font-size: 13px;
    pointer-events: none; z-index: 5;
  }

  /* ── RIGHT PANEL: details ── */
  .trk-right {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: calc(100vh - 195px);
    min-height: 500px;
    overflow-y: auto;
  }
  .trk-right::-webkit-scrollbar { width: 4px; }
  .trk-right::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }

  .trk-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    flex-shrink: 0;
  }
  .trk-card-title {
    font-size: 10px; font-weight: 700;
    color: #9ca3af; text-transform: uppercase;
    letter-spacing: 1px; margin-bottom: 11px;
    padding-bottom: 9px; border-bottom: 1px solid #f3f4f6;
  }
  .trk-kpi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
  .trk-kpi {
    background: #f9fafb; border: 1px solid #f3f4f6;
    border-radius: 9px; padding: 10px 8px; text-align: center;
  }
  .trk-kpi-val { font-size: 18px; font-weight: 700; line-height: 1.1; color: #111827; }
  .trk-kpi-val.blue   { color: #3b82f6; }
  .trk-kpi-val.orange { color: #f59e0b; }
  .trk-kpi-val.green  { color: #22c55e; }
  .trk-kpi-val.purple { color: #8b5cf6; }
  .trk-kpi-label { font-size: 9px; color: #9ca3af; margin-top: 2px; font-weight: 500; }
  .trk-tl { display: flex; flex-direction: column; }
  .trk-tl-item { display: flex; align-items: flex-start; gap: 9px; padding: 7px 0; }
  .trk-tl-item:not(:last-child) { border-bottom: 1px solid #f9fafb; }
  .trk-tl-dot {
    width: 26px; height: 26px; flex-shrink: 0;
    border-radius: 7px; display: flex; align-items: center;
    justify-content: center; font-size: 10px; font-weight: 700;
  }
  .trk-tl-dot.a { background: #dcfce7; color: #16a34a; }
  .trk-tl-dot.b { background: #dbeafe; color: #2563eb; }
  .trk-tl-info { flex: 1; }
  .trk-tl-type  { font-size: 9px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
  .trk-tl-time  { font-size: 14px; font-weight: 700; color: #111827; }
  .trk-tl-date  { font-size: 10px; color: #6b7280; }
  .trk-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 6px 0; border-bottom: 1px solid #f9fafb;
  }
  .trk-row:last-child { border-bottom: none; }
  .trk-row-label { font-size: 11px; color: #6b7280; }
  .trk-row-val   { font-size: 11px; font-weight: 600; color: #111827; }
  .trk-row-val.blue { color: #3b82f6; }
  .trk-no-select {
    display: flex; align-items: center; justify-content: center;
    flex-direction: column; gap: 8px; color: #9ca3af;
    font-size: 12px; min-height: 200px;
  }


`;


const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 13,
    padding: "0 4px",
    boxShadow: "none",
    border: state.isFocused ? "1px solid #2684FF" : "1px solid #d1d5db",
    "&:hover": {
      borderColor: "#60a5fa",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 4px",
  }),
  input: (base) => ({
    ...base,
    padding: "2px 0",
    margin: 0,
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 13,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 13,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 34,
    padding: "0 3px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  option: (base) => ({
    ...base,
    fontSize: 13,
    padding: "6px 10px",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 200,
    overflowY: "auto",
    scrollBehavior: "smooth",
    "::-webkit-scrollbar": {
      width: "8px",
    },
    "::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-thumb": {
      background: "#c9c9c9",
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-thumb:hover": {
      background: "#888",
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#3b82f6",
    color: "white",
    fontSize: 12,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "white",
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "white",
    ":hover": {
      backgroundColor: "#ef4444",
      color: "white",
    },
  }),
};
const TrackingMap = () => {
  const navigate = useNavigate();

  const [customer, setCustomer] = useLocalStorageState("customer");

  const customerId = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    authentication: rf,
    Authorization: token,
  };

  const userId = customer?.customer_details?.id;
  const user_type = customer.customer_details?.user_type;

  // ── localStorage keys (per user) ─────────────────────────────────
  const SELECTED_TRIP_KEY = `tracking_selected_trip_${userId}`;
  const OWNER_ID_KEY = `tracking_owner_id_${userId}`;
  const APPLIED_FROM_KEY = `tracking_applied_from_${userId}`;
  const APPLIED_TO_KEY = `tracking_applied_to_${userId}`;

  const savedOwnerId = localStorage.getItem(OWNER_ID_KEY) || "all";

  const { control, setValue, watch } = useForm({
    defaultValues: { ownerId: savedOwnerId },
  });

  const extras = { customer, setCustomer, rf, navigate };
  const [tripsList, setTripsList] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(localStorage.getItem(APPLIED_FROM_KEY) || "");
  const [toDate, setToDate] = useState(localStorage.getItem(APPLIED_TO_KEY) || "");
  const [appliedFrom, setAppliedFrom] = useState(localStorage.getItem(APPLIED_FROM_KEY) || "");
  const [appliedTo, setAppliedTo] = useState(localStorage.getItem(APPLIED_TO_KEY) || "");

  // Filter popup
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);

  // Server-side pagination
  const [limit, setLimit] = useState(5);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(9);
  const [ownerOptions, setOwnerOptions] = useState([]);
  // Pagination (local page for left panel display)
  const [currentPage, setCurrentPage] = useState(1);

  const selectedTripIdRef = useRef(localStorage.getItem(SELECTED_TRIP_KEY) || null);
  const isFirstLoad = useRef(!localStorage.getItem(SELECTED_TRIP_KEY));
  const mapRef = useRef(null);
  const id = watch("ownerId");

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyAWhlCsGz6qHWNA2WrGeWu-yKQAEtRtZp8",
  });

  const buildApiUrl = (from, to, lim, off, ownerId) => {
    const base = `${baseURL}gps/fetch_gps_tracking/${customerId}/${userId}/${user_type}`;
    const resolvedOwnerId = ownerId !== undefined ? ownerId : (id || localStorage.getItem(OWNER_ID_KEY) || "all");
    const params = new URLSearchParams({
      limit: lim,
      offset: off,
      owner_id: resolvedOwnerId,
    });
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    return `${base}?${params.toString()}`;
  };

  // ── Fetch trips ──────────────────────────────────────────────────
  const fetchTrips = async (
    from = appliedFrom,
    to = appliedTo,
    lim = limit,
    off = offset,
    ownerId,
  ) => {
    try {
      const url = buildApiUrl(from, to, lim, off, ownerId);
      const res = await GetRequest(url, { headers }, extras);
      console.log(res);
      const trips = res?.data || [];
      const total = res?.total_count;
      console.log(trips, "in side ");
      setTripsList(trips);
      console.log(total, "total");
      setTotalCount(total);

      if (isFirstLoad.current && trips.length > 0) {
        const latest = trips.reduce((a, b) =>
          parseInt(a.start_time) > parseInt(b.start_time) ? a : b,
        );
        selectedTripIdRef.current = String(latest.id);
        localStorage.setItem(SELECTED_TRIP_KEY, String(latest.id));
        setSelectedTrip(latest);
        isFirstLoad.current = false;
      } else if (!isFirstLoad.current && selectedTripIdRef.current) {
        const savedId = selectedTripIdRef.current;
        const refreshed = trips.find(
          (t) => String(t.id) === savedId,
        );
        if (refreshed) {
          setSelectedTrip(refreshed);
        } else {
          // saved trip not in current page, keep selectedTrip as-is (don't reset)
        }
      }
    } catch (err) {
      console.error("Failed to fetch trips:", err);
    }
  };

  useEffect(() => {
    if (Number(user_type) === 1) {
      const fetchOwnerOptions = async () => {
        try {
          const res = await GetRequest(
            `/users/users/fetchUserAsOwner/${customerId}`,
            { headers },
            extras,
          );
          let data = [];
          if (res?.status === "success") {
            const apiData = res.data.map((user) => ({
              id: user.id,
              label: user.full_name,
            }));
            data = [{ id: "all", label: "all" }, ...apiData];
            setOwnerOptions(data);
            // Restore saved ownerId from localStorage, fallback to "all"
            const restoredOwnerId = localStorage.getItem(OWNER_ID_KEY) || "all";
            setValue("ownerId", restoredOwnerId);
          }
        } catch (error) {
          console.error(error);
          return;
        }
      };

      if (customerId) {
        fetchOwnerOptions();
      }
    }
  }, [customerId, setValue, user_type]);
  // Initial load
  useEffect(() => {
    const savedOwnerId = localStorage.getItem(OWNER_ID_KEY) || "all";
    const savedFrom = localStorage.getItem(APPLIED_FROM_KEY) || "";
    const savedTo = localStorage.getItem(APPLIED_TO_KEY) || "";
    setLoading(true);
    fetchTrips(savedFrom, savedTo, 5, 0, savedOwnerId).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setFilteredTrips(tripsList);
  }, [tripsList]);

  const totalTrips = totalCount || filteredTrips.length;

  const totalPages = Math.max(1, Math.ceil(totalTrips / limit));
  const safePage = Math.min(currentPage, totalPages);
  const pagedTrips = [...filteredTrips].sort(
    (a, b) => parseInt(b.start_time) - parseInt(a.start_time),
  );
  const groupedTrips = groupTripsByDate(pagedTrips);
  const getSerial = (trip, idx) => offset * limit + idx + 1;
  const selectTrip = (trip) => {
    selectedTripIdRef.current = String(trip.id);
    localStorage.setItem(SELECTED_TRIP_KEY, String(trip.id));
    setSelectedTrip(trip);
  };

  const applyFilter = () => {
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
    setOffset(0);
    setCurrentPage(1);
    setFilterPopupOpen(false);
    setSelectedTrip(null);
    selectedTripIdRef.current = null;
    localStorage.removeItem(SELECTED_TRIP_KEY);
    // Save owner + date filters
    const currentOwnerId = id || "all";
    localStorage.setItem(OWNER_ID_KEY, String(currentOwnerId));
    if (fromDate) localStorage.setItem(APPLIED_FROM_KEY, fromDate); else localStorage.removeItem(APPLIED_FROM_KEY);
    if (toDate) localStorage.setItem(APPLIED_TO_KEY, toDate); else localStorage.removeItem(APPLIED_TO_KEY);
    isFirstLoad.current = true;
    setLoading(true);
    fetchTrips(fromDate, toDate, limit, 0, currentOwnerId).finally(() => setLoading(false));
  };
  const resetFilter = () => {
    setFromDate("");
    setToDate("");
    setAppliedFrom("");
    setAppliedTo("");
    setOffset(0);
    setCurrentPage(1);
    setFilterPopupOpen(false);
    setSelectedTrip(null);
    selectedTripIdRef.current = null;
    localStorage.removeItem(SELECTED_TRIP_KEY);
    localStorage.removeItem(APPLIED_FROM_KEY);
    localStorage.removeItem(APPLIED_TO_KEY);
    localStorage.setItem(OWNER_ID_KEY, "all");
    setValue("ownerId", "all");
    isFirstLoad.current = true;
    setLoading(true);
    fetchTrips("", "", limit, 0, "all").finally(() => setLoading(false));
  };

  const handlePageChange = (page) => {
    const newOffset = page - 1;
    setCurrentPage(page);
    setOffset(newOffset);
    setLoading(true);
    fetchTrips(appliedFrom, appliedTo, limit, newOffset, id || localStorage.getItem(OWNER_ID_KEY) || "all").finally(() =>
      setLoading(false),
    );
  };

  // ── Route computation ───────────────────────────────────────────
  const routePoints = selectedTrip ? buildRouteFromTrip(selectedTrip) : [];
  const startPoint = routePoints[0];
  const endPoint = routePoints[routePoints.length - 1];
  const distanceKm = selectedTrip
    ? (parseFloat(selectedTrip.distance || 0) / 1000).toFixed(2)
    : "0.00";
  const duration = selectedTrip
    ? calcDuration(selectedTrip.start_time, selectedTrip.end_time)
    : "--";
  const mapCenter = startPoint || { lat: 18.52, lng: 73.85 };

  const fitMapToRoute = (map, points) => {
    if (!map || !window.google) return;
    if (points.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds);
    } else if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(15);
    }
  };
  const handleMapLoad = (map) => {
    mapRef.current = map;
    fitMapToRoute(map, routePoints);
  };
  useEffect(() => {
    if (mapRef.current && routePoints.length > 0)
      fitMapToRoute(mapRef.current, routePoints);
  }, [selectedTrip]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pagination component ────────────────────────────────────────
  const renderPagination = () => {
    const pages = [];
    let start = Math.max(1, safePage - 1);
    let end = Math.min(totalPages, start + 2);
    if (end - start < 2) start = Math.max(1, end - 2);

    if (start > 1) {
      pages.push(
        <button
          key="1"
          className="trk-page-btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>,
      );
      if (start > 2)
        pages.push(
          <span
            key="e1"
            style={{ fontSize: 11, color: "#9ca3af", padding: "0 2px" }}
          >
            …
          </span>,
        );
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`trk-page-btn${safePage === i ? " active" : ""}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>,
      );
    }
    if (end < totalPages) {
      if (end < totalPages - 1)
        pages.push(
          <span
            key="e2"
            style={{ fontSize: 11, color: "#9ca3af", padding: "0 2px" }}
          >
            …
          </span>,
        );
      pages.push(
        <button
          key={totalPages}
          className="trk-page-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>,
      );
    }
    return pages;
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      <div className="trk-root">
        {/* TOP BAR */}
        <div className="trk-topbar">
          <div className="trk-topbar-left">
            <div className="trk-icon">📍</div>
            <div>
              <div className="trk-title">GPS Tracker</div>
              <div className="trk-sub">Live CRM Tracking Dashboard</div>
            </div>
          </div>
          <div className="trk-topbar-right">
            {loading && (
              <div className="trk-syncing">
                <div
                  className="trk-spinner"
                  style={{ width: 14, height: 14, borderWidth: 2 }}
                />
                Syncing…
              </div>
            )}
            <div className="trk-live">
              <div className="trk-live-dot" />
              Live Sync
            </div>
          </div>
        </div>

        {/* FILTER POPUP */}
        {filterPopupOpen && (
          <div
            className="trk-filter-overlay"
            style={{ justifyContent: "flex-start", paddingLeft: 20 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setFilterPopupOpen(false);
            }}
          >
            <div className="trk-filter-popup">
              <div className="trk-popup-title">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                Filter by Date Range
              </div>

              <div className="trk-popup-field">
                <div className="trk-popup-label">From</div>
                <input
                  type="date"
                  className="trk-date-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="trk-popup-field">
                <div className="trk-popup-label">To</div>
                <input
                  type="date"
                  className="trk-date-input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              {Number(user_type) === 1 && (
                <>
                  <hr />
                  <FormGroup>
                    <FormLabel>Users</FormLabel>
                    <Controller
                      name="ownerId"
                      control={control}
                      render={({ field: { onChange, value, ref } }) => (
                        <Select
                          inputRef={ref}
                          options={ownerOptions}
                          styles={customSelectStyles}
                          getOptionLabel={(option) => option.label}
                          getOptionValue={(option) => option.id}
                          value={
                            ownerOptions.find(
                              (option) => option.id === value,
                            ) || null
                          }
                          onChange={(val) => onChange(val ? val.id : "")}
                          placeholder="Select Owner..."
                          isClearable={false}
                        />
                      )}
                    />
                  </FormGroup>
                </>
              )}
              <div className="trk-popup-actions">
                <button className="trk-filter-btn reset" onClick={resetFilter}>
                  Reset
                </button>
                <button className="trk-filter-btn apply" onClick={applyFilter}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 3-PANEL */}
        <div className="trk-panels">
          {/* ── LEFT: trip list ── */}
          <div className="trk-left">
            <div
              className="trk-left-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                All Trips
                {totalTrips > 0 && (
                  <span
                    style={{
                      background: "#dbeafe",
                      color: "#1d4ed8",
                      borderRadius: 10,
                      padding: "1px 7px",
                      fontSize: 10,
                    }}
                  >
                    {totalTrips}
                  </span>
                )}
              </div>
              <button
                className={`trk-filter-icon-btn${appliedFrom || appliedTo ? " active" : ""}`}
                style={{ padding: "3px 9px", fontSize: 11 }}
                onClick={() => setFilterPopupOpen((v) => !v)}
                title="Filter by date"
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                {(appliedFrom || appliedTo) && (
                  <span className="trk-filter-dot" />
                )}
              </button>
            </div>

            <div className="trk-list-scroll">
              {pagedTrips.length === 0 ? (
                <div
                  style={{
                    padding: "30px 14px",
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: 12,
                  }}
                >
                  No trips found
                </div>
              ) : (
                Object.entries(groupedTrips).map(([dateLabel, trips]) => (
                  <div key={dateLabel} className="trk-date-group">
                    <div className="trk-date-group-label">
                      {dateLabel}
                      <span className="trk-date-badge">{trips.length}</span>
                    </div>
                    {trips.map((trip, idx) => {
                      const isActive =
                        String(trip.id) === String(selectedTrip?.id);
                      const km = (
                        parseFloat(trip.distance || 0) / 1000
                      ).toFixed(1);
                      return (
                        <div
                          key={trip.id}
                          className={`trk-trip-row${isActive ? " active" : ""}`}
                          onClick={() => selectTrip(trip)}
                        >
                          <div className="trk-trip-num">
                            #{offset + idx + 1}
                          </div>
                          <div className="trk-trip-body">
                            <div className="trk-trip-mode">
                              {trip.mode === "bike" ? "🏍" : "🚗"}{" "}
                              {trip.mode || "Car"}
                            </div>
                            <div className="trk-trip-times">
                              {formatTime(trip.start_time)} →{" "}
                              {formatTime(trip.end_time)}
                            </div>
                            {km > 0 && (
                              <div className="trk-trip-dist">{km} km</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <div className="trk-pagination">
              <div className="trk-page-info">
                Page <b>{safePage}</b> / <b>{totalPages}</b>
              </div>
              <div className="trk-page-btns">
                <button
                  className="trk-page-btn"
                  onClick={() => handlePageChange(Math.max(1, safePage - 1))}
                  disabled={safePage === 1}
                >
                  ‹
                </button>
                {renderPagination()}
                <button
                  className="trk-page-btn"
                  onClick={() =>
                    handlePageChange(Math.min(totalPages, safePage + 1))
                  }
                  disabled={safePage === totalPages}
                >
                  ›
                </button>
              </div>
            </div>
          </div>

          {/* ── CENTER: MAP ── */}
          <div className="trk-center">
            {!isLoaded || (loading && tripsList.length === 0) ? (
              <div className="trk-empty-map">
                <div className="trk-spinner" />
                <span>Loading map…</span>
              </div>
            ) : loadError ? (
              <div className="trk-empty-map" style={{ color: "#ef4444" }}>
                ⚠ Failed to load Google Maps
              </div>
            ) : (
              <>
                <div className="trk-map-chips">
                  <div className="trk-chip blue">
                    📡 {routePoints.length} GPS pts
                  </div>
                  {selectedTrip && (
                    <div className="trk-chip">
                      {selectedTrip.mode === "bike" ? "🏍" : "🚗"}{" "}
                      {selectedTrip.mode || "Car"}
                    </div>
                  )}
                  {selectedTrip && (
                    <div className="trk-chip">
                      Trip #{getSerial(selectedTrip)} ·{" "}
                      {formatDate(selectedTrip.start_time)}
                    </div>
                  )}
                </div>
                {selectedTrip && routePoints.length <= 1 && (
                  <div className="trk-no-route">
                    <span style={{ fontSize: 28 }}>🗺</span>
                    <span>Only 1 GPS point — no route to draw</span>
                  </div>
                )}
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={13}
                  onLoad={handleMapLoad}
                  options={{
                    zoomControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                  }}
                >
                  {startPoint && (
                    <MarkerF
                      position={startPoint}
                      label={{
                        text: "A",
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: "12px",
                      }}
                      title="Start Point"
                    />
                  )}
                  {routePoints.length > 1 && (
                    <>
                      <Polyline
                        path={routePoints}
                        options={{
                          strokeColor: "#2563eb",
                          strokeOpacity: 0.9,
                          strokeWeight: 5,
                        }}
                      />
                      {endPoint && (
                        <MarkerF
                          position={endPoint}
                          label={{
                            text: "B",
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                          title="End Point"
                        />
                      )}
                    </>
                  )}
                </GoogleMap>
              </>
            )}
          </div>

          {/* ── RIGHT: details ── */}
          <div className="trk-right">
            {!selectedTrip ? (
              <div className="trk-card">
                <div className="trk-no-select">
                  <span style={{ fontSize: 32 }}>👈</span>
                  <span>Select a trip to view details</span>
                </div>
              </div>
            ) : (
              <>
                {/* KPI */}
                <div className="trk-card">
                  <div className="trk-card-title">Quick Stats</div>
                  <div className="trk-kpi-grid">
                    <div className="trk-kpi">
                      <div className="trk-kpi-val blue">{distanceKm}</div>
                      <div className="trk-kpi-label">km traveled</div>
                    </div>
                    <div className="trk-kpi">
                      <div className="trk-kpi-val orange">
                        {selectedTrip.speed || 0}
                      </div>
                      <div className="trk-kpi-label">km/h max</div>
                    </div>
                    <div className="trk-kpi">
                      <div className="trk-kpi-val green">
                        {routePoints.length}
                      </div>
                      <div className="trk-kpi-label">GPS points</div>
                    </div>
                    <div className="trk-kpi">
                      <div className="trk-kpi-val purple">{duration}</div>
                      <div className="trk-kpi-label">duration</div>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="trk-card">
                  <div className="trk-card-title">Route Timeline</div>
                  <div className="trk-tl">
                    <div className="trk-tl-item">
                      <div className="trk-tl-dot a">A</div>
                      <div className="trk-tl-info">
                        <div className="trk-tl-type">Departure</div>
                        <div className="trk-tl-time">
                          {formatTime(selectedTrip.start_time)}
                        </div>
                        <div className="trk-tl-date">
                          {formatDate(selectedTrip.start_time)}
                        </div>
                      </div>
                    </div>
                    <div className="trk-tl-item">
                      <div className="trk-tl-dot b">B</div>
                      <div className="trk-tl-info">
                        <div className="trk-tl-type">Arrival</div>
                        <div className="trk-tl-time">
                          {formatTime(selectedTrip.end_time)}
                        </div>
                        <div className="trk-tl-date">
                          {formatDate(selectedTrip.end_time)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trip Details */}
                <div className="trk-card">
                  <div className="trk-card-title">Trip Details</div>
                  <div className="trk-row">
                    <span className="trk-row-label">Trip #</span>
                    <span className="trk-row-val blue">
                      #{getSerial(selectedTrip)}
                    </span>
                  </div>
                  <div className="trk-row">
                    <span className="trk-row-label">Mode</span>
                    <span className="trk-row-val">
                      {selectedTrip.mode || "Car"}
                    </span>
                  </div>
                  <div className="trk-row">
                    <span className="trk-row-label">Distance</span>
                    <span className="trk-row-val">{distanceKm} km</span>
                  </div>
                  <div className="trk-row">
                    <span className="trk-row-label">Max Speed</span>
                    <span className="trk-row-val">
                      {selectedTrip.speed || 0} km/h
                    </span>
                  </div>
                  <div className="trk-row">
                    <span className="trk-row-label">GPS Points</span>
                    <span className="trk-row-val">{routePoints.length}</span>
                  </div>
                  {selectedTrip.status && (
                    <div className="trk-row">
                      <span className="trk-row-label">Status</span>
                      <span
                        className="trk-row-val"
                        style={{ color: "#22c55e" }}
                      >
                        {selectedTrip.status}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TrackingMap;